import type { HostHubTodoCardData } from "../../../types/model/todo";
import type {
  MessagesSearchResponse,
  MessagesSearchThread,
  UnreadMessageCountResponse,
} from "../../../../services/messages.service";
import { Links } from "../../../../constants/links.consts";
import {
  MAX_LISTING_THUMBNAILS,
  SLOW_RESPONDER_HOURS_THRESHOLD,
  UNREAD_MESSAGES_MIN_COUNT,
} from "./converter.consts";

export const isSlowResponderHours = (hours?: number): boolean =>
  (hours ?? 0) > SLOW_RESPONDER_HOURS_THRESHOLD;

const emptyCard = (primaryCtaRoute: string): HostHubTodoCardData => ({
  isEligible: false,
  primaryCtaRoute,
});

/**
 * Picks up to `MAX_LISTING_THUMBNAILS` distinct listing thumbnails (small
 * variant) from the threads, ordered by the response. Dedupe key is the
 * inner `listing_id` so two threads on the same listing only contribute one
 * thumbnail to the stack.
 */
const collectUniqueListingThumbnails = (
  threads: MessagesSearchThread[],
): { mediaAlt?: string; mediaUrls: string[] } => {
  const seenListingIds = new Set<string>();
  const mediaUrls: string[] = [];
  let mediaAlt: string | undefined;

  for (const { listing, thread } of threads) {
    const listingId = thread?.listing_id;
    const url = listing?.thumbnail?.url_small;
    if (!listingId || !url || seenListingIds.has(listingId)) continue;

    seenListingIds.add(listingId);
    mediaUrls.push(url);
    if (mediaAlt === undefined) mediaAlt = listing?.title;
    if (mediaUrls.length >= MAX_LISTING_THUMBNAILS) break;
  }

  return { mediaAlt, mediaUrls };
};

export const toBookingRequestsAndInquiriesCard = (
  response: MessagesSearchResponse | undefined,
  _hostSsoId?: string,
  potentialPayout?: number | null,
  currency?: string,
): HostHubTodoCardData => {
  if (!response) return emptyCard(Links.Inbox);

  const countIsMonetary = potentialPayout != null;
  const count = countIsMonetary ? potentialPayout : response.total;
  const { mediaAlt, mediaUrls } = collectUniqueListingThumbnails(
    response.threads ?? [],
  );

  return {
    isEligible: countIsMonetary ? (count ?? 0) > 0 : response.total > 0,
    count,
    countIsMonetary,
    ...(countIsMonetary && currency ? { currency } : {}),
    ...(mediaUrls.length > 0 ? { mediaAlt, mediaUrls } : {}),
    primaryCtaRoute: Links.InboxBookingRequestsAndInquiries,
  };
};

export const toReviewBookingUpdateCards = (
  response: MessagesSearchResponse | undefined,
  hostSsoId: string | undefined,
): HostHubTodoCardData[] => {
  if (!response) return [];

  return (response.threads ?? []).map(({ listing, participants, thread }) => {
    const guest = participants?.find(
      (participant) => participant.ssoid !== hostSsoId,
    );
    const guestName = [guest?.name?.first, guest?.name?.last]
      .filter(Boolean)
      .join(" ")
      .trim();

    return {
      avatarAlt: guestName || undefined,
      avatarUrl: guest?.avatar_url,
      isEligible: thread?.booking?.state === "GUEST_UPDATE_REQUESTED",
      mediaAlt: listing?.title,
      mediaUrl: listing?.thumbnail?.url_small,
      primaryCtaRoute: Links.InboxUpdateRequested,
      secondaryData: guestName || undefined,
      sourceTimestamp: thread?.booking?.starts_at,
    };
  });
};

export const toUnreadMessagesCard = (
  response: UnreadMessageCountResponse | undefined,
): HostHubTodoCardData => {
  if (!response) return emptyCard(Links.Inbox);

  const count = response.thread_count ?? 0;

  return {
    isEligible: count >= UNREAD_MESSAGES_MIN_COUNT,
    count,
    primaryCtaRoute: Links.InboxUnreadMessages,
  };
};
